import { useRef, useState } from 'react';
import type { ParticipationItemType } from '../../functions/src/itemParticipationPolicy.mjs';
import { changeItemParticipation } from '../services/itemParticipation';
import { userMessage } from '../utils/userMessage';
import { ERROR_BOX } from './UI';

/** Inside the existing editor: no disabled booking fields or second modal. */
export function ItemParticipationForm({ scopeId, type, item, title, date, userId, onClose, onSavingChange }: {
  scopeId: string; type: ParticipationItemType; item: { id?: string; participantIds?: string[] };
  title: string; date?: string; userId: string; onClose: () => void; onSavingChange: (saving: boolean) => void;
}) {
  const [before] = useState(!item.participantIds || item.participantIds.includes(userId));
  const [joining, setJoining] = useState(before);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef('');
  return <form className="space-y-5" onSubmit={async event => {
    event.preventDefault();
    if (saving || !item.id) return;
    if (joining === before) { onClose(); return; }
    requestId.current ||= crypto.randomUUID();
    setSaving(true); onSavingChange(true); setError('');
    try {
      await changeItemParticipation(scopeId, { type, itemId: item.id, ...(date ? { date } : {}), before, joining, requestId: requestId.current });
      onSavingChange(false); onClose();
    } catch (error) { setError(userMessage(error, 'Couldn’t save your participation. Try again.')); }
    finally { setSaving(false); onSavingChange(false); }
  }}>
    <h3 className="ds-title">{title}</h3>
    <fieldset disabled={saving || !!requestId.current} className="space-y-3">
      <legend className="text-sm font-medium text-app-sub mb-2">Your participation</legend>
      <label className="flex items-center gap-3 min-h-11 text-sm text-app-text">
        <input type="checkbox" className="accent-gold w-4 h-4" checked={joining}
          onChange={event => { setJoining(event.target.checked); requestId.current = ''; setError(''); }} />
        I’m joining
      </label>
      <p className="text-sm text-app-sub">Bookings and expenses stay unchanged. The person who added this item will be notified.</p>
    </fieldset>
    {!item.id && <p role="status" className="text-sm text-app-sub">Ask the person who added this item to update it first.</p>}
    {!!error && <div role="alert" className={ERROR_BOX}>{error}</div>}
    <div className="flex gap-2 pt-4">
      <button type="button" disabled={saving} onClick={onClose} className="flex-1 py-3 rounded-xl border border-app-border text-[11px] font-black uppercase tracking-widest text-app-sub">Cancel</button>
      <button type="submit" disabled={saving || !item.id} className="flex-1 py-3 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 disabled:opacity-40">{saving ? 'Saving…' : 'Save'}</button>
    </div>
  </form>;
}
